import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import MenuItem from '@mui/material/MenuItem'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import FormHelperText from '@mui/material/FormHelperText'
import Grid from '@mui/material/Grid2'
import React, { useState, useMemo } from 'react'
import CardContentText from 'Components/reusable/CaleoCustomComponents/CardContentText'
import { InputField } from 'Components/reusable/InputFields/InputField'
import {
  IParsedJSON,
  IParsedSkill,
  IParsedLanguageSkill,
  IParsedCombinedWorkHistory,
  IParsedCombinedEducation,
  IParsedWorkHistoryTranslation,
  IParsedEducationTranslation,
  IParsedCombinedProject,
  IParsedProjectTranslation,
  IParsedCombinedCertificate,
  IParsedCertificateTranslation,
  IParsedCombinedCourse,
  IParsedCourseTranslation,
  IParsedCombinedReference,
  IParsedReferenceTranslation,
  IParsedCombinedLink,
  IParsedLinkTranslation,
} from 'types/cvParserInterfaces'
import { LanguagesData } from 'api/UtilsAPI'
import { useTranslation } from 'react-i18next'
import { convertToDisplayDate, languageLevels, getDateFormatByLocaleAndResolution } from 'utils/utils'
import { LANGUAGE_IDS } from 'constants/caleo-constants'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import DoneIcon from '@mui/icons-material/Done'
import CloseIcon from '@mui/icons-material/Close'
import DateRangeIcon from '@mui/icons-material/DateRange'
import SkillLevelChip from 'Components/reusable/CaleoCustomComponents/SkillLevelChip'
import CaleoDatePicker from 'Components/reusable/InputFields/CaleoDatePicker'
import { formatParsedExperience } from 'utils/parsedProfileFields'
import ExperienceInputs from './ExperienceInputs'
import ParsedNamesField from './ParsedNamesField'
import ParsedNamesTable from './ParsedNamesTable'
import ParsedCompetencesTable from './ParsedCompetencesTable'
import NewOptionsPanel from './NewOptionsPanel'
import ParsedOptionStatus from './ParsedOptionStatus'
import {
  validateSkill,
  validateWorkHistory,
  validateProject,
  validateEducation,
  validateCertificate,
  validateCourse,
  validateReference,
  validateLink,
  hasValidationErrors,
  ISkillValidationErrors,
  IWorkHistoryValidationErrors,
  IProjectValidationErrors,
  IEducationValidationErrors,
  ICertificateValidationErrors,
  ICourseValidationErrors,
  IReferenceValidationErrors,
  ILinkValidationErrors,
} from 'utils/editDataValidation'

/** @notExported */
interface IEditDataProps {
  /** Raw parsed data */
  data: IParsedJSON
  /** Function to update parsed data */
  setData: (data: IParsedJSON) => void
  /** Languages data from UtilsAPI */
  languages: LanguagesData
  /** If parsed for existing person */
  existingPerson?: boolean
}

/**
 * Component for displaying parsed CV data
 *
 * @returns Parsed CV data
 * @notExported
 **/
const EditData: React.FC<IEditDataProps> = ({ data, setData, languages, existingPerson = false }) => {
  const { t, i18n } = useTranslation()
  const [editingSkillIndex, setEditingSkillIndex] = useState<number | null>(null)
  const [editingLanguageIndex, setEditingLanguageIndex] = useState<number | null>(null)
  const [editingWorkHistoryIndex, setEditingWorkHistoryIndex] = useState<number | null>(null)
  const [editingProjectIndex, setEditingProjectIndex] = useState<number | null>(null)
  const [editingEducationIndex, setEditingEducationIndex] = useState<number | null>(null)
  const [editingCourseIndex, setEditingCourseIndex] = useState<number | null>(null)
  const [editingCertificateIndex, setEditingCertificateIndex] = useState<number | null>(null)
  const [editingReferenceIndex, setEditingReferenceIndex] = useState<number | null>(null)
  const [editingLinkIndex, setEditingLinkIndex] = useState<number | null>(null)
  const [editingAbout, setEditingAbout] = useState<boolean>(false)
  const [tempAbout, setTempAbout] = useState<string>('')
  const [tempWorkHistory, setTempWorkHistory] = useState<IParsedCombinedWorkHistory | null>(null)
  const [tempProject, setTempProject] = useState<IParsedCombinedProject | null>(null)
  const [tempEducation, setTempEducation] = useState<IParsedCombinedEducation | null>(null)
  const [tempCourse, setTempCourse] = useState<IParsedCombinedCourse | null>(null)
  const [tempCertificate, setTempCertificate] = useState<IParsedCombinedCertificate | null>(null)
  const [tempReference, setTempReference] = useState<IParsedCombinedReference | null>(null)
  const [tempLink, setTempLink] = useState<IParsedCombinedLink | null>(null)

  // Validation errors state
  const [skillErrors, setSkillErrors] = useState<ISkillValidationErrors>({})
  const [workHistoryErrors, setWorkHistoryErrors] = useState<IWorkHistoryValidationErrors>({})
  const [projectErrors, setProjectErrors] = useState<IProjectValidationErrors>({})
  const [educationErrors, setEducationErrors] = useState<IEducationValidationErrors>({})
  const [courseErrors, setCourseErrors] = useState<ICourseValidationErrors>({})
  const [certificateErrors, setCertificateErrors] = useState<ICertificateValidationErrors>({})
  const [referenceErrors, setReferenceErrors] = useState<IReferenceValidationErrors>({})
  const [linkErrors, setLinkErrors] = useState<ILinkValidationErrors>({})

  // Map languageId to language code (fi, en, sv)
  const languageIdToCode = useMemo(() => {
    const reverseMap: Record<number, string> = {}
    Object.entries(LANGUAGE_IDS).forEach(([code, id]) => {
      reverseMap[id] = code
    })
    return reverseMap
  }, [])

  // Get unique language IDs from all translations
  const availableLanguages = useMemo(() => {
    const languageIds = new Set<number>()
    data.cv.translations.forEach(tr => languageIds.add(tr.languageId))
    return Array.from(languageIds).sort()
  }, [data.cv.translations])

  // Set default language based on current UI language
  const defaultLanguageId = useMemo(() => {
    const currentLangId = LANGUAGE_IDS[i18n.language as keyof typeof LANGUAGE_IDS]
    // If current UI language is in available translations, use it; otherwise use first available
    return availableLanguages.includes(currentLangId) ? currentLangId : availableLanguages[0]
  }, [i18n.language, availableLanguages])

  const [selectedLanguageId, setSelectedLanguageId] = useState<number>(defaultLanguageId)

  const renderTranslations = <T,>(translations: T[], getText: (translation: T) => string): string => {
    const filteredTranslations = translations.filter(
      tr => (tr as { languageId?: number }).languageId === selectedLanguageId
    )

    return filteredTranslations
      .map(translation => {
        const text = getText(translation)
        return text || ''
      })
      .filter(Boolean)
      .join('\n')
  }

  return (
    <Grid container spacing={3}>
      <Grid size={12}>
        <CardContentText>{t('admin.parse.result.info')}</CardContentText>
      </Grid>
      <Grid size={12}>
        <InputField
          select
          label={t('admin.parse.result.displayLanguage')}
          value={selectedLanguageId}
          onChange={e => setSelectedLanguageId(Number(e.target.value))}
          insetLabel
        >
          {availableLanguages.map(langId => {
            const langCode = languageIdToCode[langId]
            const langName = langCode && languages?.[langCode] ? languages[langCode] : `Language ${langId}`
            return (
              <MenuItem key={langId} value={langId}>
                {langName}
              </MenuItem>
            )
          })}
        </InputField>
      </Grid>
      <Grid size={12}>
        <InputField
          fullWidth
          label={t('admin.parse.result.title')}
          value={data.cv.translations.find(tr => tr.languageId === selectedLanguageId)?.primaryRole ?? ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const updatedTranslations = data.cv.translations.map(tr =>
              tr.languageId === selectedLanguageId ? { ...tr, primaryRole: e.target.value } : tr
            )
            setData({ ...data, cv: { ...data.cv, translations: updatedTranslations } })
          }}
          disabled={existingPerson}
          insetLabel
          data-testid="field-admin-parse-title"
        />
      </Grid>
      <Grid size={12}>
        {!editingAbout ? (
          <div style={{ position: 'relative' }}>
            <InputField
              multiline
              grow
              fullWidth
              label={t('admin.parse.result.about')}
              value={renderTranslations(data.cv.translations, translation => translation.about)}
              disabled
              insetLabel
            />
            {!existingPerson && (
              <IconButton
                data-testid="button-admin-parse-about-edit"
                aria-label={t('admin.parse.result.editAbout')}
                size="small"
                onClick={() => {
                  setEditingAbout(true)
                  const aboutText = renderTranslations(data.cv.translations, translation => translation.about)
                  setTempAbout(aboutText)
                }}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <InputField
              multiline
              grow
              fullWidth
              label={t('admin.parse.result.about')}
              value={tempAbout}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTempAbout(e.target.value)}
              insetLabel
            />
            <div style={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 4 }}>
              <IconButton
                data-testid="button-admin-parse-about-save"
                aria-label={t('admin.parse.result.saveAbout')}
                size="small"
                onClick={() => {
                  // Update the about field for the selected language
                  const updatedTranslations = data.cv.translations.map(tr =>
                    tr.languageId === selectedLanguageId ? { ...tr, about: tempAbout } : tr
                  )
                  setData({ ...data, cv: { ...data.cv, translations: updatedTranslations } })
                  setEditingAbout(false)
                }}
                color="primary"
              >
                <DoneIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setEditingAbout(false)}
                data-testid="button-admin-parse-about-cancel"
                aria-label={t('admin.parse.result.cancelAbout')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        )}
      </Grid>
      {!existingPerson && (
        <Grid size={12}>
          <NewOptionsPanel
            cv={data.cv}
            onChange={optionChoices => setData({ ...data, cv: { ...data.cv, optionChoices } })}
            languageId={selectedLanguageId}
          />
        </Grid>
      )}
      <Grid size={12}>
        <CardContentText type="darkSubHeader">{t('admin.parse.result.skills')}</CardContentText>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.parse.result.skill')}</TableCell>
              <TableCell>{t('admin.parse.result.level')}</TableCell>
              <TableCell>{t('admin.parse.result.experienceMonths')}</TableCell>
              {!existingPerson && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.cv.skills.map((skill: IParsedSkill, index: number) => {
              const isEditing = editingSkillIndex === index
              return (
                <TableRow key={index}>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          value={skill.name}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const updatedSkills = [...data.cv.skills]
                            updatedSkills[index] = { ...skill, name: e.target.value }
                            setData({ ...data, cv: { ...data.cv, skills: updatedSkills } })
                            // Validate on change
                            const errors = validateSkill(updatedSkills[index])
                            setSkillErrors(errors)
                          }}
                          fullWidth
                          error={skillErrors.name}
                        />
                        {skillErrors.name && <FormHelperText error>{skillErrors.name}</FormHelperText>}
                      </div>
                    ) : (
                      <>
                        {skill.name}
                        {!existingPerson && <ParsedOptionStatus cv={data.cv} kind="skill" names={[skill.name]} />}
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        select
                        value={skill.level ?? 0}
                        onChange={e => {
                          const updatedSkills = [...data.cv.skills]
                          updatedSkills[index] = {
                            ...skill,
                            level: e.target.value ? parseInt(e.target.value) : undefined,
                          }
                          setData({ ...data, cv: { ...data.cv, skills: updatedSkills } })
                        }}
                        fullWidth
                      >
                        <MenuItem value="1">{t('profile.skill.fundamental')}</MenuItem>
                        <MenuItem value="2">{t('profile.skill.novice')}</MenuItem>
                        <MenuItem value="3">{t('profile.skill.intermediate')}</MenuItem>
                        <MenuItem value="4">{t('profile.skill.advanced')}</MenuItem>
                        <MenuItem value="5">{t('profile.skill.expert')}</MenuItem>
                      </InputField>
                    ) : (
                      <SkillLevelChip level={skill.level ?? 0} type="skill" />
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <ExperienceInputs
                        value={skill}
                        onChange={experience => {
                          const updatedSkills = [...data.cv.skills]
                          updatedSkills[index] = { ...skill, ...experience }
                          setData({ ...data, cv: { ...data.cv, skills: updatedSkills } })
                        }}
                        testIdPrefix={`field-admin-parse-skill-${index}`}
                      />
                    ) : (
                      formatParsedExperience(skill, t)
                    )}
                  </TableCell>
                  {!existingPerson && (
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-skill-save-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingSkillIndex(null)
                              setSkillErrors({})
                            }}
                            color="primary"
                            disabled={hasValidationErrors(skillErrors)}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-skill-cancel-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingSkillIndex(null)
                              setSkillErrors({})
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-skill-edit-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingSkillIndex(index)
                              const errors = validateSkill(skill)
                              setSkillErrors(errors)
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-skill-delete-${index}`}
                            size="small"
                            onClick={() => {
                              const updatedSkills = data.cv.skills.filter((_, i) => i !== index)
                              setData({ ...data, cv: { ...data.cv, skills: updatedSkills } })
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Grid>
      {(['roles', 'industries'] as const).map(kind => (
        <Grid size={12} key={kind}>
          <ParsedNamesTable
            title={t(`admin.parse.result.${kind}`)}
            items={data.cv[kind] ?? []}
            onChange={items => setData({ ...data, cv: { ...data.cv, [kind]: items } })}
            languageId={selectedLanguageId}
            readOnly={existingPerson}
            testIdPrefix={kind}
            renderStatus={
              existingPerson
                ? undefined
                : item => (
                    <ParsedOptionStatus
                      cv={data.cv}
                      kind={kind === 'roles' ? 'role' : 'industry'}
                      names={item.translations.map(tr => tr.name)}
                    />
                  )
            }
          />
        </Grid>
      ))}
      <Grid size={12}>
        <CardContentText type="darkSubHeader">{t('admin.parse.result.workHistory')}</CardContentText>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.parse.result.organization')}</TableCell>
              <TableCell>{t('admin.parse.result.startDate')}</TableCell>
              <TableCell>{t('admin.parse.result.endDate')}</TableCell>
              <TableCell>{t('admin.parse.result.role')}</TableCell>
              <TableCell>{t('admin.parse.result.description')}</TableCell>
              {!existingPerson && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.cv.workHistories.map((workHistory: IParsedCombinedWorkHistory, index: number) => {
              const isEditing = editingWorkHistoryIndex === index
              const displayWorkHistory = isEditing && tempWorkHistory ? tempWorkHistory : workHistory
              const currentTranslation = displayWorkHistory.translations.find(
                tr => tr.languageId === selectedLanguageId
              )

              return (
                <TableRow key={index}>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          value={currentTranslation?.name || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempWorkHistory) {
                              const updatedTranslations = tempWorkHistory.translations.map(tr =>
                                tr.languageId === selectedLanguageId ? { ...tr, name: e.target.value } : tr
                              )
                              const updated = { ...tempWorkHistory, translations: updatedTranslations }
                              setTempWorkHistory(updated)
                              // Validate on change
                              const errors = validateWorkHistory(updated, selectedLanguageId)
                              setWorkHistoryErrors(errors)
                            }
                          }}
                          fullWidth
                          error={workHistoryErrors.name}
                        />
                        {workHistoryErrors.name && <FormHelperText error>{workHistoryErrors.name}</FormHelperText>}
                      </div>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          workHistory.translations,
                          (translation: IParsedWorkHistoryTranslation) => translation.name
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <CaleoDatePicker
                        selected={displayWorkHistory.startDate ? new Date(displayWorkHistory.startDate) : null}
                        onChange={(date: Date | null) => {
                          if (tempWorkHistory) {
                            setTempWorkHistory({
                              ...tempWorkHistory,
                              startDate: date ?? undefined,
                            })
                          }
                        }}
                        dateFormat={getDateFormatByLocaleAndResolution(
                          i18n.language,
                          displayWorkHistory.dateResolution ?? 'day'
                        )}
                        customInput={
                          <TextField
                            data-testid="field-admin-parse-work-history-start-date"
                            size="small"
                            fullWidth
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        }
                        showMonthDropdown
                        showYearDropdown
                        popperPlacement="bottom-start"
                      />
                    ) : displayWorkHistory.startDate ? (
                      convertToDisplayDate(displayWorkHistory.startDate, displayWorkHistory.dateResolution)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <CaleoDatePicker
                        selected={displayWorkHistory.endDate ? new Date(displayWorkHistory.endDate) : null}
                        onChange={(date: Date | null) => {
                          if (tempWorkHistory) {
                            setTempWorkHistory({
                              ...tempWorkHistory,
                              endDate: date ?? undefined,
                            })
                          }
                        }}
                        dateFormat={getDateFormatByLocaleAndResolution(
                          i18n.language,
                          displayWorkHistory.dateResolution ?? 'day'
                        )}
                        customInput={
                          <TextField
                            data-testid="field-admin-parse-work-history-end-date"
                            size="small"
                            fullWidth
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        }
                        showMonthDropdown
                        showYearDropdown
                        popperPlacement="bottom-start"
                      />
                    ) : displayWorkHistory.endDate ? (
                      convertToDisplayDate(displayWorkHistory.endDate, displayWorkHistory.dateResolution)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        value={currentTranslation?.role || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempWorkHistory) {
                            const updatedTranslations = tempWorkHistory.translations.map(tr =>
                              tr.languageId === selectedLanguageId ? { ...tr, role: e.target.value } : tr
                            )
                            setTempWorkHistory({ ...tempWorkHistory, translations: updatedTranslations })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          workHistory.translations,
                          (translation: IParsedWorkHistoryTranslation) => translation.role
                        )}
                      </span>
                    )}
                    <ParsedNamesField
                      label={t('admin.parse.result.roles')}
                      items={displayWorkHistory.roles ?? []}
                      onChange={roles => tempWorkHistory && setTempWorkHistory({ ...tempWorkHistory, roles })}
                      languageId={selectedLanguageId}
                      editing={isEditing}
                      testId={`field-admin-parse-work-history-roles-${index}`}
                    />
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          multiline
                          value={currentTranslation?.description || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempWorkHistory) {
                              const updatedTranslations = tempWorkHistory.translations.map(tr =>
                                tr.languageId === selectedLanguageId ? { ...tr, description: e.target.value } : tr
                              )
                              const updated = { ...tempWorkHistory, translations: updatedTranslations }
                              setTempWorkHistory(updated)
                              // Validate on change
                              const errors = validateWorkHistory(updated, selectedLanguageId)
                              setWorkHistoryErrors(errors)
                            }
                          }}
                          fullWidth
                          error={workHistoryErrors.description}
                        />
                        {workHistoryErrors.description && (
                          <FormHelperText error>{workHistoryErrors.description}</FormHelperText>
                        )}
                      </div>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          workHistory.translations,
                          (translation: IParsedWorkHistoryTranslation) => translation.description
                        )}
                      </span>
                    )}
                  </TableCell>
                  {!existingPerson && (
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-work-history-save-${index}`}
                            size="small"
                            onClick={() => {
                              if (tempWorkHistory) {
                                const updatedWorkHistories = [...data.cv.workHistories]
                                updatedWorkHistories[index] = tempWorkHistory
                                setData({ ...data, cv: { ...data.cv, workHistories: updatedWorkHistories } })
                              }
                              setEditingWorkHistoryIndex(null)
                              setTempWorkHistory(null)
                              setWorkHistoryErrors({})
                            }}
                            color="primary"
                            disabled={hasValidationErrors(workHistoryErrors)}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-work-history-cancel-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingWorkHistoryIndex(null)
                              setTempWorkHistory(null)
                              setWorkHistoryErrors({})
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-work-history-edit-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingWorkHistoryIndex(index)
                              setTempWorkHistory({ ...workHistory })
                              const errors = validateWorkHistory(workHistory, selectedLanguageId)
                              setWorkHistoryErrors(errors)
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-work-history-delete-${index}`}
                            size="small"
                            onClick={() => {
                              const updatedWorkHistories = data.cv.workHistories.filter((_, i) => i !== index)
                              setData({ ...data, cv: { ...data.cv, workHistories: updatedWorkHistories } })
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Grid>
      <Grid size={12}>
        <CardContentText type="darkSubHeader">{t('admin.parse.result.projects')}</CardContentText>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.parse.result.name')}</TableCell>
              <TableCell>{t('admin.parse.result.customer')}</TableCell>
              <TableCell>{t('admin.parse.result.employer')}</TableCell>
              <TableCell>{t('admin.parse.result.role')}</TableCell>
              <TableCell>{t('admin.parse.result.startDate')}</TableCell>
              <TableCell>{t('admin.parse.result.endDate')}</TableCell>
              <TableCell>{t('admin.parse.result.skills')}</TableCell>
              <TableCell>{t('admin.parse.result.description')}</TableCell>
              {!existingPerson && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.cv.projects.map((project: IParsedCombinedProject, index: number) => {
              const isEditing = editingProjectIndex === index
              const displayProject = isEditing && tempProject ? tempProject : project
              const currentTranslation = displayProject.translations.find(tr => tr.languageId === selectedLanguageId)

              return (
                <TableRow key={index}>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          value={currentTranslation?.name || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempProject) {
                              const updatedTranslations = tempProject.translations.map(tr =>
                                tr.languageId === selectedLanguageId ? { ...tr, name: e.target.value } : tr
                              )
                              const updated = { ...tempProject, translations: updatedTranslations }
                              setTempProject(updated)
                              const errors = validateProject(updated, selectedLanguageId)
                              setProjectErrors(errors)
                            }
                          }}
                          fullWidth
                          error={projectErrors.name}
                        />
                        {projectErrors.name && <FormHelperText error>{projectErrors.name}</FormHelperText>}
                      </div>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          project.translations,
                          (translation: IParsedProjectTranslation) => translation.name
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        value={currentTranslation?.customer || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempProject) {
                            const updatedTranslations = tempProject.translations.map(tr =>
                              tr.languageId === selectedLanguageId ? { ...tr, customer: e.target.value } : tr
                            )
                            setTempProject({ ...tempProject, translations: updatedTranslations })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          project.translations,
                          (translation: IParsedProjectTranslation) => translation.customer
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        value={currentTranslation?.employer || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempProject) {
                            const updatedTranslations = tempProject.translations.map(tr =>
                              tr.languageId === selectedLanguageId ? { ...tr, employer: e.target.value } : tr
                            )
                            setTempProject({ ...tempProject, translations: updatedTranslations })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          project.translations,
                          (translation: IParsedProjectTranslation) => translation.employer
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        value={currentTranslation?.role || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempProject) {
                            const updatedTranslations = tempProject.translations.map(tr =>
                              tr.languageId === selectedLanguageId ? { ...tr, role: e.target.value } : tr
                            )
                            setTempProject({ ...tempProject, translations: updatedTranslations })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          project.translations,
                          (translation: IParsedProjectTranslation) => translation.role
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <CaleoDatePicker
                        selected={displayProject.startDate ? new Date(displayProject.startDate) : null}
                        onChange={(date: Date | null) => {
                          if (tempProject) {
                            setTempProject({
                              ...tempProject,
                              startDate: date ?? undefined,
                            })
                          }
                        }}
                        dateFormat={getDateFormatByLocaleAndResolution(
                          i18n.language,
                          displayProject.dateResolution ?? 'day'
                        )}
                        customInput={
                          <TextField
                            data-testid="field-admin-parse-project-start-date"
                            size="small"
                            fullWidth
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        }
                        showMonthDropdown
                        showYearDropdown
                        popperPlacement="bottom-start"
                      />
                    ) : displayProject.startDate ? (
                      convertToDisplayDate(displayProject.startDate, displayProject.dateResolution)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <CaleoDatePicker
                        selected={displayProject.endDate ? new Date(displayProject.endDate) : null}
                        onChange={(date: Date | null) => {
                          if (tempProject) {
                            setTempProject({
                              ...tempProject,
                              endDate: date ?? undefined,
                            })
                          }
                        }}
                        dateFormat={getDateFormatByLocaleAndResolution(
                          i18n.language,
                          displayProject.dateResolution ?? 'day'
                        )}
                        customInput={
                          <TextField
                            data-testid="field-admin-parse-project-end-date"
                            size="small"
                            fullWidth
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        }
                        showMonthDropdown
                        showYearDropdown
                        popperPlacement="bottom-start"
                      />
                    ) : displayProject.endDate ? (
                      convertToDisplayDate(displayProject.endDate, displayProject.dateResolution)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        value={displayProject.skills?.join(', ') || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempProject) {
                            setTempProject({
                              ...tempProject,
                              skills: e.target.value
                                .split(',')
                                .map(s => s.trim())
                                .filter(s => s.length > 0),
                            })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      displayProject.skills?.join(', ')
                    )}
                    {(['roles', 'industries'] as const).map(kind => (
                      <ParsedNamesField
                        key={kind}
                        label={t(`admin.parse.result.${kind}`)}
                        items={displayProject[kind] ?? []}
                        onChange={items => tempProject && setTempProject({ ...tempProject, [kind]: items })}
                        languageId={selectedLanguageId}
                        editing={isEditing}
                        testId={`field-admin-parse-project-${kind}-${index}`}
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          multiline
                          value={currentTranslation?.description || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempProject) {
                              const updatedTranslations = tempProject.translations.map(tr =>
                                tr.languageId === selectedLanguageId ? { ...tr, description: e.target.value } : tr
                              )
                              const updated = { ...tempProject, translations: updatedTranslations }
                              setTempProject(updated)
                              const errors = validateProject(updated, selectedLanguageId)
                              setProjectErrors(errors)
                            }
                          }}
                          fullWidth
                          error={projectErrors.description}
                        />
                        {projectErrors.description && (
                          <FormHelperText error>{projectErrors.description}</FormHelperText>
                        )}
                      </div>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          project.translations,
                          (translation: IParsedProjectTranslation) => translation.description
                        )}
                      </span>
                    )}
                  </TableCell>
                  {!existingPerson && (
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-project-save-${index}`}
                            size="small"
                            onClick={() => {
                              if (tempProject) {
                                const updatedProjects = [...data.cv.projects]
                                updatedProjects[index] = tempProject
                                setData({ ...data, cv: { ...data.cv, projects: updatedProjects } })
                              }
                              setEditingProjectIndex(null)
                              setTempProject(null)
                              setProjectErrors({})
                            }}
                            color="primary"
                            disabled={hasValidationErrors(projectErrors)}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-project-cancel-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingProjectIndex(null)
                              setTempProject(null)
                              setProjectErrors({})
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-project-edit-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingProjectIndex(index)
                              setTempProject({ ...project })
                              const errors = validateProject(project, selectedLanguageId)
                              setProjectErrors(errors)
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-project-delete-${index}`}
                            size="small"
                            onClick={() => {
                              const updatedProjects = data.cv.projects.filter((_, i) => i !== index)
                              setData({ ...data, cv: { ...data.cv, projects: updatedProjects } })
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Grid>
      <Grid size={12}>
        <CardContentText type="darkSubHeader">{t('admin.parse.result.education')}</CardContentText>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.parse.result.school')}</TableCell>
              <TableCell>{t('admin.parse.result.degree')}</TableCell>
              <TableCell>{t('admin.parse.result.startDate')}</TableCell>
              <TableCell>{t('admin.parse.result.endDate')}</TableCell>
              {!existingPerson && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.cv.education.map((education: IParsedCombinedEducation, index: number) => {
              const isEditing = editingEducationIndex === index
              const displayEducation = isEditing && tempEducation ? tempEducation : education
              const currentTranslation = displayEducation.translations.find(tr => tr.languageId === selectedLanguageId)

              return (
                <TableRow key={index}>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          value={currentTranslation?.school || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempEducation) {
                              const updatedTranslations = tempEducation.translations.map(tr =>
                                tr.languageId === selectedLanguageId ? { ...tr, school: e.target.value } : tr
                              )
                              const updated = { ...tempEducation, translations: updatedTranslations }
                              setTempEducation(updated)
                              const schoolErrors = validateEducation(updated, selectedLanguageId)
                              setEducationErrors(schoolErrors)
                            }
                          }}
                          fullWidth
                          error={educationErrors.school}
                        />
                        {educationErrors.school && <FormHelperText error>{educationErrors.school}</FormHelperText>}
                      </div>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          education.translations,
                          (translation: IParsedEducationTranslation) => translation.school
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          value={currentTranslation?.degree || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempEducation) {
                              const updatedTranslations = tempEducation.translations.map(tr =>
                                tr.languageId === selectedLanguageId ? { ...tr, degree: e.target.value } : tr
                              )
                              const updated = { ...tempEducation, translations: updatedTranslations }
                              setTempEducation(updated)
                              const degreeErrors = validateEducation(updated, selectedLanguageId)
                              setEducationErrors(degreeErrors)
                            }
                          }}
                          fullWidth
                          error={educationErrors.degree}
                        />
                        {educationErrors.degree && <FormHelperText error>{educationErrors.degree}</FormHelperText>}
                      </div>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          education.translations,
                          (translation: IParsedEducationTranslation) => translation.degree
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <CaleoDatePicker
                        selected={displayEducation.startDate ? new Date(displayEducation.startDate) : null}
                        onChange={(date: Date | null) => {
                          if (tempEducation) {
                            setTempEducation({
                              ...tempEducation,
                              startDate: date ?? undefined,
                            })
                          }
                        }}
                        dateFormat={getDateFormatByLocaleAndResolution(
                          i18n.language,
                          displayEducation.dateResolution ?? 'day'
                        )}
                        customInput={
                          <TextField
                            data-testid="field-admin-parse-education-start-date"
                            size="small"
                            fullWidth
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        }
                        showMonthDropdown
                        showYearDropdown
                        popperPlacement="bottom-start"
                      />
                    ) : displayEducation.startDate ? (
                      convertToDisplayDate(displayEducation.startDate, displayEducation.dateResolution)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <CaleoDatePicker
                        selected={displayEducation.endDate ? new Date(displayEducation.endDate) : null}
                        onChange={(date: Date | null) => {
                          if (tempEducation) {
                            setTempEducation({
                              ...tempEducation,
                              endDate: date ?? undefined,
                            })
                          }
                        }}
                        dateFormat={getDateFormatByLocaleAndResolution(
                          i18n.language,
                          displayEducation.dateResolution ?? 'day'
                        )}
                        customInput={
                          <TextField
                            data-testid="field-admin-parse-education-end-date"
                            size="small"
                            fullWidth
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        }
                        showMonthDropdown
                        showYearDropdown
                        popperPlacement="bottom-start"
                      />
                    ) : displayEducation.endDate ? (
                      convertToDisplayDate(displayEducation.endDate, displayEducation.dateResolution)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  {!existingPerson && (
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-education-save-${index}`}
                            size="small"
                            onClick={() => {
                              if (tempEducation) {
                                const updatedEducation = [...data.cv.education]
                                updatedEducation[index] = tempEducation
                                setData({ ...data, cv: { ...data.cv, education: updatedEducation } })
                              }
                              setEditingEducationIndex(null)
                              setTempEducation(null)
                              setEducationErrors({})
                            }}
                            color="primary"
                            disabled={hasValidationErrors(educationErrors)}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-education-cancel-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingEducationIndex(null)
                              setTempEducation(null)
                              setEducationErrors({})
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-education-edit-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingEducationIndex(index)
                              setTempEducation({ ...education })
                              const errors = validateEducation(education, selectedLanguageId)
                              setEducationErrors(errors)
                            }}
                            disabled={hasValidationErrors(educationErrors)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-education-delete-${index}`}
                            size="small"
                            onClick={() => {
                              const updatedEducation = data.cv.education.filter((_, i) => i !== index)
                              setData({ ...data, cv: { ...data.cv, education: updatedEducation } })
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Grid>
      <Grid size={12}>
        <CardContentText type="darkSubHeader">{t('admin.parse.result.courses')}</CardContentText>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.parse.result.name')}</TableCell>
              <TableCell>{t('admin.parse.result.provider')}</TableCell>
              <TableCell>{t('admin.parse.result.description')}</TableCell>
              <TableCell>{t('admin.parse.result.startDate')}</TableCell>
              <TableCell>{t('admin.parse.result.endDate')}</TableCell>
              {!existingPerson && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.cv.courses.map((course: IParsedCombinedCourse, index: number) => {
              const isEditing = editingCourseIndex === index
              const displayCourse = isEditing && tempCourse ? tempCourse : course
              const currentTranslation = displayCourse.translations.find(tr => tr.languageId === selectedLanguageId)

              return (
                <TableRow key={index}>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          value={currentTranslation?.name || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempCourse) {
                              const updatedTranslations = tempCourse.translations.map(tr =>
                                tr.languageId === selectedLanguageId ? { ...tr, name: e.target.value } : tr
                              )
                              const updated = { ...tempCourse, translations: updatedTranslations }
                              setTempCourse(updated)
                              const errors = validateCourse(updated, selectedLanguageId)
                              setCourseErrors(errors)
                            }
                          }}
                          fullWidth
                          error={courseErrors.name}
                        />
                        {courseErrors.name && <FormHelperText error>{courseErrors.name}</FormHelperText>}
                      </div>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          course.translations,
                          (translation: IParsedCourseTranslation) => translation.name
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        value={currentTranslation?.provider || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempCourse) {
                            const updatedTranslations = tempCourse.translations.map(tr =>
                              tr.languageId === selectedLanguageId ? { ...tr, provider: e.target.value } : tr
                            )
                            setTempCourse({ ...tempCourse, translations: updatedTranslations })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          course.translations,
                          (translation: IParsedCourseTranslation) => translation.provider
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        multiline
                        value={currentTranslation?.description || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempCourse) {
                            const updatedTranslations = tempCourse.translations.map(tr =>
                              tr.languageId === selectedLanguageId ? { ...tr, description: e.target.value } : tr
                            )
                            setTempCourse({ ...tempCourse, translations: updatedTranslations })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          course.translations,
                          (translation: IParsedCourseTranslation) => translation.description
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <CaleoDatePicker
                        selected={displayCourse.startDate ? new Date(displayCourse.startDate) : null}
                        onChange={(date: Date | null) => {
                          if (tempCourse && date) {
                            setTempCourse({
                              ...tempCourse,
                              startDate: date,
                            })
                          }
                        }}
                        dateFormat={getDateFormatByLocaleAndResolution(
                          i18n.language,
                          displayCourse.dateResolution ?? 'day'
                        )}
                        customInput={
                          <TextField
                            data-testid="field-admin-parse-course-start-date"
                            size="small"
                            fullWidth
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        }
                        showMonthDropdown
                        showYearDropdown
                        popperPlacement="bottom-start"
                      />
                    ) : displayCourse.startDate ? (
                      convertToDisplayDate(displayCourse.startDate, displayCourse.dateResolution)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <CaleoDatePicker
                        selected={displayCourse.endDate ? new Date(displayCourse.endDate) : null}
                        onChange={(date: Date | null) => {
                          if (tempCourse) {
                            setTempCourse({
                              ...tempCourse,
                              endDate: date ?? undefined,
                            })
                          }
                        }}
                        dateFormat={getDateFormatByLocaleAndResolution(
                          i18n.language,
                          displayCourse.dateResolution ?? 'day'
                        )}
                        customInput={
                          <TextField
                            data-testid="field-admin-parse-course-end-date"
                            size="small"
                            fullWidth
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        }
                        showMonthDropdown
                        showYearDropdown
                        popperPlacement="bottom-start"
                      />
                    ) : displayCourse.endDate ? (
                      convertToDisplayDate(displayCourse.endDate, displayCourse.dateResolution)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  {!existingPerson && (
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-course-save-${index}`}
                            size="small"
                            onClick={() => {
                              if (tempCourse) {
                                const updatedCourses = [...data.cv.courses]
                                updatedCourses[index] = tempCourse
                                setData({ ...data, cv: { ...data.cv, courses: updatedCourses } })
                              }
                              setEditingCourseIndex(null)
                              setTempCourse(null)
                              setCourseErrors({})
                            }}
                            color="primary"
                            disabled={hasValidationErrors(courseErrors)}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-course-cancel-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingCourseIndex(null)
                              setTempCourse(null)
                              setCourseErrors({})
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-course-edit-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingCourseIndex(index)
                              setTempCourse({ ...course })
                              const errors = validateCourse(course, selectedLanguageId)
                              setCourseErrors(errors)
                            }}
                            disabled={hasValidationErrors(courseErrors)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-course-delete-${index}`}
                            size="small"
                            onClick={() => {
                              const updatedCourses = data.cv.courses.filter((_, i) => i !== index)
                              setData({ ...data, cv: { ...data.cv, courses: updatedCourses } })
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Grid>
      <Grid size={12}>
        <CardContentText type="darkSubHeader">{t('admin.parse.result.certificates')}</CardContentText>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.parse.result.name')}</TableCell>
              <TableCell>{t('admin.parse.result.issuer')}</TableCell>
              <TableCell>{t('admin.parse.result.issueDate')}</TableCell>
              <TableCell>{t('admin.parse.result.expirationDate')}</TableCell>
              <TableCell>{t('admin.parse.result.licenseNumber')}</TableCell>
              <TableCell>{t('admin.parse.result.url')}</TableCell>
              {!existingPerson && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.cv.certificates.map((certificate: IParsedCombinedCertificate, index: number) => {
              const isEditing = editingCertificateIndex === index
              const displayCertificate = isEditing && tempCertificate ? tempCertificate : certificate
              const currentTranslation = displayCertificate.translations.find(
                tr => tr.languageId === selectedLanguageId
              )

              return (
                <TableRow key={index}>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          value={currentTranslation?.name || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempCertificate) {
                              const updatedTranslations = tempCertificate.translations.map(tr =>
                                tr.languageId === selectedLanguageId ? { ...tr, name: e.target.value } : tr
                              )
                              const updated = { ...tempCertificate, translations: updatedTranslations }
                              setTempCertificate(updated)
                              const errors = validateCertificate(updated, selectedLanguageId)
                              setCertificateErrors(errors)
                            }
                          }}
                          fullWidth
                          error={certificateErrors.name}
                        />
                        {certificateErrors.name && <FormHelperText error>{certificateErrors.name}</FormHelperText>}
                      </div>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          certificate.translations,
                          (translation: IParsedCertificateTranslation) => translation.name
                        )}
                      </span>
                    )}
                    {!isEditing && !existingPerson && (
                      <ParsedOptionStatus
                        cv={data.cv}
                        kind="certificate"
                        names={certificate.translations.map(tr => tr.name)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        value={currentTranslation?.issuer || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempCertificate) {
                            const updatedTranslations = tempCertificate.translations.map(tr =>
                              tr.languageId === selectedLanguageId ? { ...tr, issuer: e.target.value } : tr
                            )
                            setTempCertificate({ ...tempCertificate, translations: updatedTranslations })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          certificate.translations,
                          (translation: IParsedCertificateTranslation) => translation.issuer
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <CaleoDatePicker
                        selected={displayCertificate.issueDate ? new Date(displayCertificate.issueDate) : null}
                        onChange={(date: Date | null) => {
                          if (tempCertificate && date) {
                            setTempCertificate({
                              ...tempCertificate,
                              issueDate: date,
                            })
                          }
                        }}
                        dateFormat={getDateFormatByLocaleAndResolution(
                          i18n.language,
                          displayCertificate.dateResolution ?? 'day'
                        )}
                        customInput={
                          <TextField
                            data-testid="field-admin-parse-certificate-issue-date"
                            size="small"
                            fullWidth
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        }
                        showMonthDropdown
                        showYearDropdown
                        popperPlacement="bottom-start"
                      />
                    ) : displayCertificate.issueDate ? (
                      convertToDisplayDate(displayCertificate.issueDate, displayCertificate.dateResolution)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <CaleoDatePicker
                        selected={
                          displayCertificate.expirationDate ? new Date(displayCertificate.expirationDate) : null
                        }
                        onChange={(date: Date | null) => {
                          if (tempCertificate) {
                            setTempCertificate({
                              ...tempCertificate,
                              expirationDate: date ?? undefined,
                            })
                          }
                        }}
                        dateFormat={getDateFormatByLocaleAndResolution(
                          i18n.language,
                          displayCertificate.dateResolution ?? 'day'
                        )}
                        customInput={
                          <TextField
                            data-testid="field-admin-parse-certificate-expiration-date"
                            size="small"
                            fullWidth
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <DateRangeIcon color="secondary" style={{ cursor: 'pointer' }} />
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        }
                        showMonthDropdown
                        showYearDropdown
                        popperPlacement="bottom-start"
                      />
                    ) : displayCertificate.expirationDate ? (
                      convertToDisplayDate(displayCertificate.expirationDate, displayCertificate.dateResolution)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        value={displayCertificate.licenseNumber || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempCertificate) {
                            setTempCertificate({ ...tempCertificate, licenseNumber: e.target.value })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      displayCertificate.licenseNumber
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        value={displayCertificate.url || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempCertificate) {
                            setTempCertificate({ ...tempCertificate, url: e.target.value })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      displayCertificate.url
                    )}
                  </TableCell>
                  {!existingPerson && (
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-certificate-save-${index}`}
                            size="small"
                            onClick={() => {
                              if (tempCertificate) {
                                const updatedCertificates = [...data.cv.certificates]
                                updatedCertificates[index] = tempCertificate
                                setData({ ...data, cv: { ...data.cv, certificates: updatedCertificates } })
                              }
                              setEditingCertificateIndex(null)
                              setTempCertificate(null)
                              setCertificateErrors({})
                            }}
                            color="primary"
                            disabled={hasValidationErrors(certificateErrors)}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-certificate-cancel-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingCertificateIndex(null)
                              setTempCertificate(null)
                              setCertificateErrors({})
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-certificate-edit-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingCertificateIndex(index)
                              setTempCertificate({ ...certificate })
                              const errors = validateCertificate(certificate, selectedLanguageId)
                              setCertificateErrors(errors)
                            }}
                            disabled={hasValidationErrors(certificateErrors)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-certificate-delete-${index}`}
                            size="small"
                            onClick={() => {
                              const updatedCertificates = data.cv.certificates.filter((_, i) => i !== index)
                              setData({ ...data, cv: { ...data.cv, certificates: updatedCertificates } })
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Grid>
      {/* Left out by the backend when the target organisation doesn't use competences */}
      {!!data.cv.competences?.length && (
        <Grid size={12}>
          <ParsedCompetencesTable
            competences={data.cv.competences}
            onChange={competences => setData({ ...data, cv: { ...data.cv, competences } })}
            languageId={selectedLanguageId}
            readOnly={existingPerson}
            renderStatus={
              existingPerson
                ? undefined
                : competence => (
                    <ParsedOptionStatus
                      cv={data.cv}
                      kind="competence"
                      names={competence.translations.map(tr => tr.name)}
                    />
                  )
            }
          />
        </Grid>
      )}
      <Grid size={12}>
        <CardContentText type="darkSubHeader">{t('admin.parse.result.languages')}</CardContentText>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.parse.result.language')}</TableCell>
              <TableCell>{t('admin.parse.result.level')}</TableCell>
              {!existingPerson && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.cv.languageSkills.map((language: IParsedLanguageSkill, index: number) => {
              const levelLabel = language.level
                ? languageLevels().find(levelValue => levelValue.level === language.level)?.label
                : undefined
              const isEditing = editingLanguageIndex === index

              return (
                <TableRow key={index}>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        select
                        value={language.languageCode}
                        onChange={e => {
                          const updatedLanguages = [...data.cv.languageSkills]
                          updatedLanguages[index] = { ...language, languageCode: e.target.value }
                          setData({ ...data, cv: { ...data.cv, languageSkills: updatedLanguages } })
                        }}
                        fullWidth
                      >
                        {Object.entries(languages || {}).map(([code, name]) => (
                          <MenuItem key={code} value={code}>
                            {name}
                          </MenuItem>
                        ))}
                      </InputField>
                    ) : (
                      languages?.[language.languageCode]
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        select
                        value={language.level || ''}
                        onChange={e => {
                          const updatedLanguages = [...data.cv.languageSkills]
                          updatedLanguages[index] = {
                            ...language,
                            level: e.target.value ? parseInt(e.target.value) : undefined,
                          }
                          setData({ ...data, cv: { ...data.cv, languageSkills: updatedLanguages } })
                        }}
                        fullWidth
                      >
                        <MenuItem value="1">{t(languageLevels()[0].label)}</MenuItem>
                        <MenuItem value="2">{t(languageLevels()[1].label)}</MenuItem>
                        <MenuItem value="3">{t(languageLevels()[2].label)}</MenuItem>
                        <MenuItem value="4">{t(languageLevels()[3].label)}</MenuItem>
                        <MenuItem value="5">{t(languageLevels()[4].label)}</MenuItem>
                      </InputField>
                    ) : language.level && levelLabel ? (
                      t(levelLabel)
                    ) : (
                      ''
                    )}
                  </TableCell>
                  {!existingPerson && (
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-language-save-${index}`}
                            size="small"
                            onClick={() => setEditingLanguageIndex(null)}
                            color="primary"
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-language-cancel-${index}`}
                            size="small"
                            onClick={() => setEditingLanguageIndex(null)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-language-edit-${index}`}
                            size="small"
                            onClick={() => setEditingLanguageIndex(index)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-language-delete-${index}`}
                            size="small"
                            onClick={() => {
                              const updatedLanguages = data.cv.languageSkills.filter((_, i) => i !== index)
                              setData({ ...data, cv: { ...data.cv, languageSkills: updatedLanguages } })
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Grid>
      <Grid size={12}>
        <CardContentText type="darkSubHeader">{t('admin.parse.result.references')}</CardContentText>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.parse.result.firstName')}</TableCell>
              <TableCell>{t('admin.parse.result.lastName')}</TableCell>
              <TableCell>{t('admin.parse.result.company')}</TableCell>
              <TableCell>{t('admin.parse.result.description')}</TableCell>
              {!existingPerson && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.cv.references.map((reference: IParsedCombinedReference, index: number) => {
              const isEditing = editingReferenceIndex === index
              const displayReference = isEditing && tempReference ? tempReference : reference
              const currentTranslation = displayReference.translations.find(tr => tr.languageId === selectedLanguageId)

              return (
                <TableRow key={index}>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          value={displayReference.firstName || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempReference) {
                              const updated = { ...tempReference, firstName: e.target.value }
                              setTempReference(updated)
                              const errors = validateReference(updated)
                              setReferenceErrors(errors)
                            }
                          }}
                          fullWidth
                          error={referenceErrors.name}
                        />
                        {referenceErrors.name && <FormHelperText error>{referenceErrors.name}</FormHelperText>}
                      </div>
                    ) : (
                      displayReference.firstName
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          value={displayReference.lastName || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempReference) {
                              const updated = { ...tempReference, lastName: e.target.value }
                              setTempReference(updated)
                              const errors = validateReference(updated)
                              setReferenceErrors(errors)
                            }
                          }}
                          fullWidth
                          error={referenceErrors.name}
                        />
                        {referenceErrors.name && <FormHelperText error>{referenceErrors.name}</FormHelperText>}
                      </div>
                    ) : (
                      displayReference.lastName
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        value={displayReference.company || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempReference) {
                            setTempReference({ ...tempReference, company: e.target.value })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      displayReference.company
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputField
                        multiline
                        value={currentTranslation?.description || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          if (tempReference) {
                            const updatedTranslations = tempReference.translations.map(tr =>
                              tr.languageId === selectedLanguageId ? { ...tr, description: e.target.value } : tr
                            )
                            setTempReference({ ...tempReference, translations: updatedTranslations })
                          }
                        }}
                        fullWidth
                      />
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          reference.translations,
                          (translation: IParsedReferenceTranslation) => translation.description
                        )}
                      </span>
                    )}
                  </TableCell>
                  {!existingPerson && (
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-reference-save-${index}`}
                            size="small"
                            onClick={() => {
                              if (tempReference) {
                                const updatedReferences = [...data.cv.references]
                                updatedReferences[index] = tempReference
                                setData({ ...data, cv: { ...data.cv, references: updatedReferences } })
                              }
                              setEditingReferenceIndex(null)
                              setTempReference(null)
                              setReferenceErrors({})
                            }}
                            color="primary"
                            disabled={hasValidationErrors(referenceErrors)}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-reference-cancel-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingReferenceIndex(null)
                              setTempReference(null)
                              setReferenceErrors({})
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-reference-edit-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingReferenceIndex(index)
                              setTempReference({ ...reference })
                              const refErrors = validateReference(reference)
                              setReferenceErrors(refErrors)
                            }}
                            disabled={hasValidationErrors(referenceErrors)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-reference-delete-${index}`}
                            size="small"
                            onClick={() => {
                              const updatedReferences = data.cv.references.filter((_, i) => i !== index)
                              setData({ ...data, cv: { ...data.cv, references: updatedReferences } })
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Grid>
      <Grid size={12}>
        <CardContentText type="darkSubHeader">{t('admin.parse.result.links')}</CardContentText>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.parse.result.url')}</TableCell>
              <TableCell>{t('admin.parse.result.description')}</TableCell>
              {!existingPerson && <TableCell width={100}>{t('admin.parse.result.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.cv.links.map((link: IParsedCombinedLink, index: number) => {
              const isEditing = editingLinkIndex === index
              const displayLink = isEditing && tempLink ? tempLink : link
              const currentTranslation = displayLink.translations.find(tr => tr.languageId === selectedLanguageId)

              return (
                <TableRow key={index}>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          value={displayLink.url || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempLink) {
                              const updated = { ...tempLink, url: e.target.value }
                              setTempLink(updated)
                              const errors = validateLink(updated)
                              setLinkErrors(errors)
                            }
                          }}
                          fullWidth
                          error={linkErrors.url}
                        />
                        {linkErrors.url && <FormHelperText error>{linkErrors.url}</FormHelperText>}
                      </div>
                    ) : (
                      displayLink.url
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <div>
                        <InputField
                          multiline
                          value={currentTranslation?.description || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            if (tempLink) {
                              const updatedTranslations = tempLink.translations.map(tr =>
                                tr.languageId === selectedLanguageId ? { ...tr, description: e.target.value } : tr
                              )
                              setTempLink({ ...tempLink, translations: updatedTranslations })
                            }
                          }}
                          fullWidth
                        />
                      </div>
                    ) : (
                      <span style={{ whiteSpace: 'pre-line' }}>
                        {renderTranslations(
                          link.translations,
                          (translation: IParsedLinkTranslation) => translation.description
                        )}
                      </span>
                    )}
                  </TableCell>
                  {!existingPerson && (
                    <TableCell>
                      {isEditing ? (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-link-save-${index}`}
                            size="small"
                            onClick={() => {
                              if (tempLink) {
                                const updatedLinks = [...data.cv.links]
                                updatedLinks[index] = tempLink
                                setData({ ...data, cv: { ...data.cv, links: updatedLinks } })
                              }
                              setEditingLinkIndex(null)
                              setTempLink(null)
                              setLinkErrors({})
                            }}
                            color="primary"
                            disabled={hasValidationErrors(linkErrors)}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-link-cancel-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingLinkIndex(null)
                              setTempLink(null)
                              setLinkErrors({})
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            data-testid={`button-admin-parse-link-edit-${index}`}
                            size="small"
                            onClick={() => {
                              setEditingLinkIndex(index)
                              setTempLink({ ...link })
                              const linkValidityErrors = validateLink(link)
                              setLinkErrors(linkValidityErrors)
                            }}
                            disabled={hasValidationErrors(linkErrors)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            data-testid={`button-admin-parse-link-delete-${index}`}
                            size="small"
                            onClick={() => {
                              const updatedLinks = data.cv.links.filter((_, i) => i !== index)
                              setData({ ...data, cv: { ...data.cv, links: updatedLinks } })
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  )
}

export default EditData
